// Helpers for tables whose header list uses the grouped, two-row shape
// (`{ name, children: [{ name, key }] }` alongside plain `{ name, key }`
// entries — see salaryRevisionGroupedHeader/salaryRevisionTableHeader in HR
// Headers) instead of a flat list. The saved-view picker (SaveViewButton)
// and the persisted headerConfig only ever work with flat `{ key, name }`
// rows, so a grouped header list is flattened to its leaf columns (tagging
// each leaf with its parent group's name) before being handed to the
// picker, and regrouped afterwards so the original two-row <TableHead>
// rendering keeps working unchanged.
//
// Some grouped tables reuse the same leaf key under multiple parents (e.g.
// salaryRevisionTableHeader's "Old Salary" and "New Salary" both have a
// child keyed "salary", disambiguated only by which group it's nested
// under). Flattening therefore identifies each leaf by `groupName::key`
// (falling back to the bare key when ungrouped) while keeping the leaf's
// real data key on `dataKey`, so regrouping can restore the original,
// possibly-duplicated key the table body actually indexes data with.

export const isGroupedHeaderList = (headers = []) =>
  headers.some((header) => Array.isArray(header.children));

export const flattenHeaders = (headers = []) =>
  headers.flatMap((header) =>
    Array.isArray(header.children)
      ? header.children.map((child) => ({
          ...child,
          dataKey: child.key,
          key: `${header.name}::${child.key}`,
          groupName: header.name,
        }))
      : [{ ...header, dataKey: header.key, groupName: null }],
  );

// Flattens an already-resolved header list (e.g. the `visibleHeaders`
// useSaveView returns, reflecting the current saved view's selection/order/
// rename) down to a plain flat list for consumers that need one — the row
// filter dropdown, CSV/Excel/PDF export — but must still honor that saved
// view. Unlike flattenHeaders, this does no id-disambiguation: by this
// point each leaf's `.key` is already its real, regrouped data key.
export const toFlatDisplayHeaders = (headers = []) =>
  isGroupedHeaderList(headers)
    ? headers.flatMap((header) =>
        Array.isArray(header.children) ? header.children : [header],
      )
    : headers;

// Re-wraps an ordered flat leaf list back into the grouped shape, merging
// consecutive leaves that share the same groupName into one spanning group
// header. Leaves reordered apart from their original siblings simply end up
// as separate group segments rather than being silently dropped.
export const regroupHeaders = (flatHeaders = []) => {
  const result = [];
  flatHeaders.forEach((leaf) => {
    const { groupName, dataKey, ...rest } = leaf;
    const restored = { ...rest, key: dataKey ?? rest.key };
    const last = result[result.length - 1];
    if (groupName && last?.name === groupName && last?.children) {
      last.children.push(restored);
    } else if (groupName) {
      result.push({ name: groupName, children: [restored] });
    } else {
      result.push(restored);
    }
  });
  return result;
};
